import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

import { subscriptionUnitToText } from '../../utils';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../../hooks/use-payment-state';
import { FormattedPrice } from '../../../../components/FormattedPrice/FormattedPrice';

export function SubscriptionsDescription({ theme }: { theme: any; isMobile: boolean }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { subscriptionData } = useCheckout();
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);

  const { pricing } = subscriptionData || {};
  // the priceNet should be taken from the pricing object because we want to display the original price before/after the discount
  // other pricing values should be taken from the priceBreakdown object of the store/firstPaymentDiscount
  const priceNet = subscriptionData?.pricing?.priceBreakdown?.baseNet;
  const priceTotalBeforeDiscount = subscriptionData?.pricing?.priceBreakdown?.priceTotal;
  const priceTotal = priceBreakdown?.priceTotal;
  const tax = priceBreakdown?.tax;
  // the number is negative because we want to subtract the discount from the total
  const discount = priceBreakdown?.discount ? -Math.abs(priceBreakdown.discount) : 0;
  const showTaxValue = priceBreakdown?.isTaxValid;

  const subscriptionDisplayName = subscriptionData?.plan?.displayName || '';
  const billingFrequency = subscriptionData?.plan?.billingFrequency;

  return (
    <div className="subscriptions-description">
      <span className="description-top-title">
        {t('subscribeTo')} {subscriptionDisplayName}
      </span>
      <Stack className="description-top-price" direction="row" alignItems="center" spacing={1}>
        <FormattedPrice
          pricing={pricing}
          priceValue={priceTotalBeforeDiscount}
          data-testid="total-price-header"
        />
        <span className="billing-period">{`${t('per')} ${billingFrequency}`}</span>
      </Stack>

      <div className="description-footer-subtotal">
        <span style={{ fontWeight: 'bold' }}>{`${subscriptionDisplayName} ${t(
          'subscription'
        )}`}</span>
        <FormattedPrice pricing={pricing} priceValue={priceTotalBeforeDiscount} />
      </div>
      <div className="description-footer-subtotal">
        <span>{`${t('billed')} ${subscriptionUnitToText(billingFrequency)}`}</span>
      </div>

      <Box mt={2}>
        <hr style={{ borderColor: theme?.text, marginBottom: 0 }} />
      </Box>

      <div className="description-lower">
        <div className="description-footer-subtotal">
          <span>{t('subtotal')}</span>
          <FormattedPrice pricing={pricing} priceValue={priceNet} data-testid="subtotal-price" />
        </div>
        {Boolean(discount) && (
          <div className="description-footer-subtotal">
            <span>{t('firstPaymentDiscount')}</span>
            <FormattedPrice pricing={pricing} priceValue={discount} data-testid="discount" />
          </div>
        )}
        <div className="description-footer-tax">
          <span data-testid="tax-percent">
            {t('tax')} {showTaxValue && <>({(priceBreakdown?.taxRatePoints || 0) / 100}%)</>}
          </span>
          {showTaxValue ? (
            <FormattedPrice pricing={pricing} priceValue={tax} data-testid="tax-price" />
          ) : (
            <span>–</span>
          )}
        </div>
        <Box mt={2} width={'100%'}>
          <hr style={{ borderColor: theme?.text, marginBottom: 0 }} />
        </Box>
        <div className="description-footer-total">
          <span>{t('totalDueToday')}</span>
          <FormattedPrice pricing={pricing} priceValue={priceTotal} data-testid="total-price" />
        </div>
      </div>
    </div>
  );
}
